import classNames from 'classnames'
import React, { useMemo } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { SeoPageRouter } from '@src/components/seo/seo-page-router'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Skeleton } from '@src/quince-components/src/atoms/skeleton'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { getSeoMetapageData, GetSeoMetapageDataProps } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'

import styles from '../../collectionPage.module.scss'

interface CollectionTitleProps {
  title: string | undefined
  hideCollectionPageTitle: boolean
  isRootCollectionPage: boolean
  isJumpLink: boolean
  subCollectionTitle?: string
  seoData: GetSeoMetapageDataProps | null
  isNewCLPFiltersExperimentActive: boolean
  showLoader?: boolean
  alternateUrls?: Record<string, string>
}

export const CollectionTitle: React.FC<CollectionTitleProps> = ({
  title,
  hideCollectionPageTitle,
  isRootCollectionPage,
  isJumpLink,
  subCollectionTitle,
  seoData,
  isNewCLPFiltersExperimentActive,
  showLoader,
  alternateUrls,
}) => {
  const isI18nPreviewEnabled = useI18nPreviewContext()
  const { deviceType } = useDeviceInfo()
  const { regionDetails } = useLocaleContext()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP

  // This is a alternative solution to update the seo data for the sub collection page, to support old collection page product type urls
  const updatedSeoData = useMemo(() => {
    if (isNewCLPFiltersExperimentActive && subCollectionTitle && seoData) {
      return getSeoMetapageData(isI18nPreviewEnabled, {
        ...seoData,
        title: `${subCollectionTitle} | Quince`,
        // TODO: Sujeet - Fix the canonical url for sub collection pages
        canonicalUrl: '', // window.location.href,
      })
    }
    return getSeoMetapageData(seoData)
  }, [isNewCLPFiltersExperimentActive, seoData, subCollectionTitle])

  const renderDesktopCollectionTitle = () => {
    if (hideCollectionPageTitle || !title) return null

    const titleClassName = `desktopOnly d-flex justify-content--center font-family--grosa font-weight--500 text-transform--uppercase ${
      styles.collection_header
    } ${isNewCLPFiltersExperimentActive ? styles.newCLPFiltersTitle : ''}`

    if (showLoader) {
      return (
        <div
          className={classNames(styles.collectionTitleLoaderContainer, {
            [`${styles.collectionTitleLoaderContainerForNewCLPFilters}`]: isNewCLPFiltersExperimentActive,
          })}
        >
          <Skeleton className={styles.collectionTitleLoader} />
        </div>
      )
    }

    return (
      <h1 data-testid={TEST_LOCATORS.COLLECTION_TITLE} className={titleClassName}>
        {title}
      </h1>
    )
  }

  const renderMobileCollectionTitle = () => {
    if (hideCollectionPageTitle || isDesktop) return null

    const titleClassName = `mobileOnly d-flex justify-content--center font-family--grosa font-weight--500 text-transform--uppercase ${
      styles.collection_header
    } ${isNewCLPFiltersExperimentActive ? styles.newCLPFiltersMobileTitle : ''} ${
      isRootCollectionPage ? styles.mobileNonCollectionPageTitle : ''
    }`

    if (showLoader) {
      return <Skeleton className={styles.collectionTitleLoader} />
    }

    return (
      <h1 data-testid={TEST_LOCATORS.COLLECTION_TITLE} className={titleClassName}>
        {isJumpLink ? subCollectionTitle : title}
      </h1>
    )
  }

  return (
    <>
      {isNewCLPFiltersExperimentActive && subCollectionTitle && (
        <SeoPageRouter seoMetaData={updatedSeoData} alternateUrls={alternateUrls} regionDetails={regionDetails} />
      )}
      {isDesktop ? renderDesktopCollectionTitle() : renderMobileCollectionTitle()}
    </>
  )
}
